import { Injectable } from "@angular/core";
import { HomeSliderDataModel, HomeSliderViewModel, HomePrioTileViewModel, HomePrioTileDataModel, HomeTileViewModel, HomeTileDataModel } from "../../shared/models/homeContent.model";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ContentType, ImageOrientation } from "../../shared/contentTypes";
import { environment } from "../../../../environments/environment";
import { UserHelperService } from "../../services/userHelper.service";
import { DataModel } from "../../shared/models/syncModel";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { IObjectObserver } from "../../services/IObjectObserver";
import { Observable } from "rxjs";

@Injectable()
export class HomeService {

  private _homeSliderObsServ: IObjectObserver<HomeSliderDataModel>;
  private _homePriorityTileObsServ: IObjectObserver<HomePrioTileDataModel>;
  private _homeTileObsServ: IObjectObserver<HomeTileDataModel>;

  constructor(private _objObsInjectHelper: CircularInjectionHelper
    , private _userHelperService: UserHelperService) {
    this._homeSliderObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<HomeSliderDataModel>(ContentType.Home_Slider, this._objObsInjectHelper);
    this._homePriorityTileObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<HomePrioTileDataModel>(ContentType.Home_PrioTile, this._objObsInjectHelper);
    this._homeTileObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<HomeTileDataModel>(ContentType.Home_Tile, this._objObsInjectHelper);

  }

  public get HomeSliderObserveChanges(): Observable<(string | number)[]> {
    return this._homeSliderObsServ.ObserveChanges;
  }

  public get HomePriorityObserveChanges(): Observable<(string | number)[]> {
    return this._homePriorityTileObsServ.ObserveChanges;
  }

  public get HomeTileObserveChanges(): Observable<(string | number)[]> {
    return this._homeTileObsServ.ObserveChanges;
  }

  /**
   * get the list of solider for home page
  * @result a list of HomeSliderViewModel
   */
  public GetHomeSliderViewModel(): Promise<HomeSliderViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<HomeSliderViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._homeSliderObsServ.GetObjectsByFilter(model => model.release && (model.public || isTestUser))
        .then(data => {
          me.mapToHomeSliderViewModelList(data)
            .then(resolve)
            .catch((e) => {
              me._objObsInjectHelper.coreLogService.error("HomeService.GetHomeSliderViewModel", "mapToHomeSliderViewModelList", e);
              reject();
            });
        })
        .catch((e) => {
          me._objObsInjectHelper.coreLogService.error("HomeService.GetHomeSliderViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
  * get the list of PriorityTile for home page
  * @result a list of HomePrioTileViewModel
  */
  public GetHomePriorityTileViewModel(): Promise<HomePrioTileViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<HomePrioTileViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._homePriorityTileObsServ.GetObjectsByFilter(model => model.release && (model.public || isTestUser))
        .then(data => {
          me.mapToPriorityTileViewModelList(data).then(resolve);
        })
        .catch((e) => {
          me._objObsInjectHelper.coreLogService.error("HomeService.GetHomePriorityViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * get the list of HomeTiles for home page
   * @result a list of HomeTileViewModel
   */
  public GetHomeTileViewModel(): Promise<HomeTileViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<HomeTileViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._homeTileObsServ.GetObjectsByFilter(model => model.release && (model.public || isTestUser))
        .then(data => {
          me.mapToHomeTileViewModelList(data).then(resolve);
        })
        .catch((e) => {
          me._objObsInjectHelper.coreLogService.error("HomeService.GetHomePriorityViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  // ****************Mapper****************

  /**
  * map HomeSliderDataModel to HomeSliderViewModel
  * @result a HomeSliderViewModel
  */
  private async mapToHomeSliderViewModel(model: HomeSliderDataModel): Promise<HomeSliderViewModel> {
    const me = this;
    const viewModel: HomeSliderViewModel = new HomeSliderViewModel();
    let contentModel: any = null;

    if (model.use_content_image || (model.use_label && model.use_content_label)) {
      if (model.content_type >= 0) {
        try {
          const _contentObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<DataModel>(model.content_type, this._objObsInjectHelper);
          contentModel = await _contentObsServ.GetObject(model.content_id);
        } catch (e) {
          me._objObsInjectHelper.coreLogService.error("HomeService.mapToHomeSliderViewModel", "GetObject", e, model);
        }
      } else {
        me._objObsInjectHelper.coreLogService.error("HomeService.mapToHomeSliderViewModel", "Use_Content but wrong ContentType", model);
      }
    }
    if (model.use_label) {
      if (model.use_content_label && contentModel) {
        viewModel.label_de = contentModel.label_de;
        viewModel.label_en = contentModel.label_en;
      } else {
        viewModel.label_de = model.label_de;
        viewModel.label_en = model.label_en;
      }
    }
    viewModel.sort_nr = model.sort_nr;
    viewModel.contentType = model.content_type;
    viewModel.contentId = model.content_id;
    viewModel.content_extra = model.content_extra;
    viewModel.imagePhoto = ContentType.Home_Slider + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    return viewModel;
  }

  /**
  * map a list of  HomeSliderDataModel to a list of HomeSliderViewModel
  * @result a list of HomeSliderViewModel
  */
  private mapToHomeSliderViewModelList(modelList: HomeSliderDataModel[]): Promise<HomeSliderViewModel[]> {
    const me = this;
    return Promise.all(modelList.map(model => me.mapToHomeSliderViewModel(model)));
  }

  /**
  * map HomePrioTileDataModel to HomePrioTileViewModel
  * @result a  HomePrioTileViewModel
  */
  private async mapToPriorityTileViewModel(model: HomePrioTileDataModel): Promise<HomePrioTileViewModel> {
    const me = this;
    const viewModel: HomePrioTileViewModel = new HomePrioTileViewModel();
    let contentModel: any = null;
    if (model.use_content_image || (model.use_label && model.use_content_label)) {
      if (model.content_type >= 0) {
        const _contentObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<DataModel>(model.content_type, this._objObsInjectHelper);
        try {
          contentModel = await _contentObsServ.GetObject(model.content_id);
        } catch (e) {
          me._objObsInjectHelper.coreLogService.error("HomeService.mapToHomeSliderViewModel", "GetObject", e);
        }
      } else {
        me._objObsInjectHelper.coreLogService.error("HomeService.mapToHomeSliderViewModel", "Use_Content but wrong ContentType", model);
      }
    }
    viewModel.sort_nr = model.sort_nr;
    viewModel.use_label = model.use_label;
    if (model.use_label) {
      if (model.use_content_label && contentModel) {
        viewModel.label_de = contentModel.label_de;
        viewModel.label_en = contentModel.label_en;
      } else {
        viewModel.label_de = model.label_de;
        viewModel.label_en = model.label_en;
      }
    }
    viewModel.contentType = model.content_type;
    viewModel.contentId = model.content_id;
    viewModel.content_extra = model.content_extra;
    if (model.use_content_image && contentModel) {
      viewModel.imagePhoto = model.content_type + "/" + model.content_id + "_photo_" + ImageOrientation.Landscape + "/" + contentModel.last_image_update_at_ymd + "/" + contentModel.last_image_update_at_hms;
    } else {
      viewModel.imagePhoto = ContentType.Home_PrioTile + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    }
    return viewModel;
  }

  /**
  * map a list of  HomePrioTileDataModel to a list of HomePrioTileViewModel
  * @result a list of HomePrioTileViewModel
  */
  private mapToPriorityTileViewModelList(modelList: HomePrioTileDataModel[]): Promise<HomePrioTileViewModel[]> {
    const me = this;
    return Promise.all(modelList.map(model => me.mapToPriorityTileViewModel(model)));
  }

  /**
  * map HomeTileDataModel to HomeTileViewModel
  * @result a HomeTileViewModel
  */
  private async mapToHomeTileViewModel(model: HomeTileDataModel): Promise<HomeTileViewModel> {
    const me = this;
    const viewModel: HomeTileViewModel = new HomeTileViewModel();
    let contentModel: any = null;
    if (model.use_label && model.use_content_label) {
      if (model.content_type >= 0) {
        const _contentObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<DataModel>(model.content_type, this._objObsInjectHelper);
        try {
          contentModel = await _contentObsServ.GetObject(model.content_id);
        } catch (e) {
          me._objObsInjectHelper.coreLogService.error("HomeService.mapToHomeSliderViewModel", "GetObject", e);
        }
      } else {
        me._objObsInjectHelper.coreLogService.error("HomeService.mapToHomeSliderViewModel", "Use_Content but wrong ContentType", model);
      }
    }
    viewModel.sort_nr = model.sort_nr;
    viewModel.use_label = model.use_label;
    if (model.use_label) {
      if (model.use_content_label && contentModel) {
        viewModel.label_de = contentModel.label_de;
        viewModel.label_en = contentModel.label_en;
      } else {
        viewModel.label_de = model.label_de;
        viewModel.label_en = model.label_en;
      }
    }
    viewModel.contentType = model.content_type;
    viewModel.contentId = model.content_id;
    viewModel.content_extra = model.content_extra;
    viewModel.imagePhoto = ContentType.Home_Tile + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    return viewModel;
  }
  /**
  * map a list of  HomeTileDataModel to a list of HomeTileViewModel
  * @result a list of HomeTileViewModel
  */
  private mapToHomeTileViewModelList(modelList: HomeTileDataModel[]): Promise<HomeTileViewModel[]> {
    const me = this;
    return Promise.all(modelList.map(model => me.mapToHomeTileViewModel(model)));
  }
}
